import React from 'react';
import {
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalTitle,
  CloseButton,
  ButtonGroup,
  PrimaryButton,
  SecondaryButton,
  EmptyState,
} from '../GenerateOffer.styles';
import { MailIcon, XIcon } from '../icons';
import { pokazDniPlatne } from '../../../utils/dniPlatne';

export function ConfirmSendModal({ isOpen, onClose, onConfirm, email, t }) {
  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()} style={{ maxWidth: '500px' }}>
        <ModalHeader>
          <ModalTitle>
            <MailIcon />
            {t('generateOffer.confirmSendTitle')}
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <XIcon />
          </CloseButton>
        </ModalHeader>

        <div style={{
          padding: '20px 0',
          fontSize: '1rem',
          color: 'var(--text)',
          lineHeight: '1.6'
        }}>
          <p style={{ marginBottom: '12px' }}>
            {t('generateOffer.confirmSendMessage')}
          </p>
          <p style={{
            fontWeight: 700,
            color: 'var(--primary)',
            fontSize: '1.1rem',
            marginBottom: 0
          }}>
            {email}
          </p>
        </div>

        <ButtonGroup style={{ marginTop: '20px' }}>
          <SecondaryButton onClick={onClose} data-cy="offer-cancel-send">
            {t('generateOffer.cancelSend')}
          </SecondaryButton>
          <PrimaryButton onClick={onConfirm} data-cy="offer-confirm-send">
            <MailIcon />
            {t('generateOffer.confirmSend')}
          </PrimaryButton>
        </ButtonGroup>
      </ModalContent>
    </ModalOverlay>
  );
}

export function ConfirmDeleteModal({ isOpen, onClose, onConfirm, offer }) {
  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent
        onClick={e => e.stopPropagation()}
        style={{
          maxWidth: '520px',
          background: 'var(--card-bg)',
          border: '2px solid var(--danger)',
          borderRadius: '14px',
          padding: '22px',
          boxShadow: '0 10px 30px rgba(0,0,0,0.12)'
        }}
      >
        <ModalHeader style={{ borderBottom: 'none', marginBottom: 12, paddingBottom: 8 }}>
          <ModalTitle style={{ color: 'var(--danger)', fontSize: '1.25rem' }}>
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginRight: 8 }}>
              <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" stroke="var(--danger)" strokeWidth="1.5"/>
              <line x1="12" y1="9" x2="12" y2="13" stroke="var(--danger)" strokeWidth="1.6"/>
              <circle cx="12" cy="17" r="1" fill="var(--danger)" />
            </svg>
            Usuń ofertę
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <XIcon />
          </CloseButton>
        </ModalHeader>

        <div style={{ padding: '6px 0 0 0', fontSize: '1rem', color: 'var(--text)', lineHeight: '1.5' }}>
          <p style={{ marginBottom: '8px', color: 'var(--text)' }}>
            Czy na pewno chcesz usunąć tą ofertę?
          </p>
          <p style={{ marginTop: 12, fontWeight: 700, color: 'var(--danger)' }}>
            Operacja jest nieodwracalna!
          </p>
        </div>

        <ButtonGroup style={{ marginTop: '18px' }}>
          <SecondaryButton onClick={onClose} style={{ background: 'var(--card-bg)', border: '1px solid var(--input-border)' }}>Anuluj</SecondaryButton>
          <PrimaryButton
            onClick={onConfirm}
            style={{
              background: 'var(--danger)',
              boxShadow: '0 6px 18px rgba(239,68,68,0.25)'
            }}
          >
            <XIcon /> Usuń
          </PrimaryButton>
        </ButtonGroup>
      </ModalContent>
    </ModalOverlay>
  );
}

export function SentOffersModal({ isOpen, onClose, offers }) {
  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <ModalTitle>
            <MailIcon />
            Wysłane oferty
          </ModalTitle>
          <CloseButton onClick={onClose}>
            <XIcon />
          </CloseButton>
        </ModalHeader>

        {offers.length === 0 ? (
          <EmptyState>
            <div className="icon">
              <MailIcon />
            </div>
            <div className="text">Nie wysłano jeszcze żadnych ofert</div>
          </EmptyState>
        ) : (
          <div style={{ maxHeight: '60vh', overflowY: 'auto' }}>
            {offers.map((offer, idx) => (
              <div
                key={idx}
                style={{
                  padding: '16px',
                  marginBottom: '12px',
                  background: 'var(--card-bg)',
                  border: '1px solid var(--input-border)',
                  borderRadius: '12px'
                }}
              >
                <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '8px' }}>
                  <strong style={{ fontSize: '1.05rem', color: 'var(--text)' }}>
                    {(() => {
                      const parts = String(offer.client_name || '').trim().split(/\s+/);
                      const first = offer.firstname || offer.firstName || (parts.length > 1 ? parts[0] : parts[0] || '');
                      const last = offer.lastname || offer.lastName || (parts.length > 1 ? parts.slice(1).join(' ') : '');
                      return `${first}${last ? ' ' + last : ''}`.trim();
                    })()}
                  </strong>
                  <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>
                    {new Date(offer.sent_at).toLocaleDateString('pl-PL', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric',
                      hour: '2-digit',
                      minute: '2-digit'
                    })}
                  </span>
                </div>

                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '8px', fontSize: '0.9rem' }}>
                  <div>
                    <span style={{ color: 'var(--text-muted)' }}>Email: </span>
                    <span style={{ color: 'var(--text)' }}>{offer.email}</span>
                  </div>
                  <div>
                    <span style={{ color: 'var(--text-muted)' }}>Termin: </span>
                    <span style={{ color: 'var(--text)' }}>
                      {new Date(offer.date_from).toLocaleDateString('pl-PL')} - {new Date(offer.date_to).toLocaleDateString('pl-PL')}
                    </span>
                  </div>
                  <div>
                    <span style={{ color: 'var(--text-muted)' }}>Kwota: </span>
                    <span style={{ fontWeight: 600, color: 'var(--primary)' }}>
                      {Number(offer.total_price || 0).toFixed(2)} zł
                    </span>
                  </div>
                  <div>
                    <span style={{ color: 'var(--text-muted)' }}>Dni: </span>
                    <span style={{ color: 'var(--text)' }}>{pokazDniPlatne(offer.days)}</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}

        <ButtonGroup style={{ marginTop: '20px' }}>
          <SecondaryButton onClick={onClose}>
            Zamknij
          </SecondaryButton>
        </ButtonGroup>
      </ModalContent>
    </ModalOverlay>
  );
}
